import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useAnalytics } from '@/composables/useAnalytics';

export const useAudioStore = defineStore('audio', () => {
  const { trackPlay } = useAnalytics();
  const currentAudio = ref(null);
  const currentlyPlayingUrl = ref(null);
  const currentlyPlayingTitle = ref(null);
  const isCurrentlyPlaying = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);

  const isPlaying = computed(() => {
    return isCurrentlyPlaying.value;
  });

  const play = (url, title = '') => {
    // If the same audio is already playing, do nothing
    if (currentlyPlayingUrl.value === url && isPlaying.value) {
      return;
    }

    // If the same URL is paused, just resume it
    if (currentlyPlayingUrl.value === url && currentAudio.value) {
      currentAudio.value.play();
      isCurrentlyPlaying.value = true;
      return;
    }

    // Stop any currently playing audio
    stop();

    // Create and play new audio
    currentAudio.value = new Audio(url);
    currentlyPlayingUrl.value = url;
    currentlyPlayingTitle.value = title;

    // Add event listeners
    currentAudio.value.addEventListener('loadedmetadata', () => {
      duration.value = currentAudio.value.duration || 0;
    });

    currentAudio.value.addEventListener('timeupdate', () => {
      currentTime.value = currentAudio.value.currentTime || 0;
    });

    currentAudio.value.addEventListener('ended', () => {
      currentlyPlayingUrl.value = null;
      currentlyPlayingTitle.value = null;
      isCurrentlyPlaying.value = false;
      currentTime.value = 0;
      duration.value = 0;
    });

    // Track playback event (only for new audio, not resume)
    if (title) {
      trackPlay(title);
    }

    currentAudio.value.play();
    isCurrentlyPlaying.value = true;
  };

  const pause = () => {
    if (currentAudio.value) {
      currentAudio.value.pause();
      isCurrentlyPlaying.value = false;
    }
  };

  const stop = () => {
    if (currentAudio.value) {
      currentAudio.value.pause();
      currentAudio.value.currentTime = 0;
      currentlyPlayingUrl.value = null;
      currentlyPlayingTitle.value = null;
      isCurrentlyPlaying.value = false;
      currentTime.value = 0;
      duration.value = 0;
    }
  };

  const seek = (time) => {
    if (currentAudio.value) {
      currentAudio.value.currentTime = time;
      currentTime.value = time;
    }
  };

  const toggle = (url, title = '') => {
    // If this URL is currently playing, pause it
    if (currentlyPlayingUrl.value === url && isPlaying.value) {
      pause();
    } else {
      play(url, title);
    }
  };

  const isUrlPlaying = (url) => {
    return currentlyPlayingUrl.value === url && isPlaying.value;
  };

  return {
    currentAudio,
    currentlyPlayingUrl,
    currentlyPlayingTitle,
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    stop,
    seek,
    toggle,
    isUrlPlaying,
  };
});
